// Library Methods
import { useCallback, useMemo } from "react";

// Components
import VFScreeningCard from "./exam-cards/VFScreeningCard/VFScreeningCard";
import VFThresholdCard from "./exam-cards/VFThresholdCard/VFThresholdCard";
import VFSuperior36Card from "./exam-cards/VFSuperior36Card/VFSuperior36Card";
import Full120Card from "./exam-cards/Full120Card/Full120Card";
import VFEstermanCard from "./exam-cards/VFEstermanCard/VFEstermanCard";
import ColorVisionCard from "./exam-cards/ColorVisionCard/ColorVisionCard";
import ContrastSensitivityCard from "./exam-cards/ContrastSensitivityCard/ContrastSensitivityCard";
import VisualAcuityCard from "./exam-cards/VisualAcuityCard/VisualAcuityCard";
import RAPDCard from "./exam-cards/RAPDCard/RAPDCard";
import OcularMotilityCard from "./exam-cards/OcularMotilityCard/OcularMotilityCard";
import DeviationAndCorrespondenceCard from "./exam-cards/DeviationAndCorrespondenceCard/DeviationAndCorrespondenceCard";
import FusionAndVergenceCard from "./exam-cards/FusionAndVergenceCard/FusionAndVergenceCard";
import StereopsisCard from "./exam-cards/StereopsisCard/StereopsisCard";
import AniseikoniaCard from "./exam-cards/AniseikoniaCard/AniseikoniaCard";

// Contexts
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";

// Constants
import { examCardEnums, eyeTypeEnums } from "src/constants/enums";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

type ExamSliderCardType = {
  id: string;
  label: string;
  groupId: number;
  examAlgorithm: string;
};

const ExamSliderCard = (props: ExamSliderCardType) => {
  const { examAlgorithm, groupId } = props;
  const { selectedExamCards, actions } = useExamSliderContext();
  const { setSelectedExamCards, onRemoveCard } = actions;

  const selectedExamCard = useMemo(
    () =>
      selectedExamCards.find(
        (examCard) =>
          examCard.examFieldType === examAlgorithm &&
          examCard.groupId === groupId
      ),
    [selectedExamCards, groupId, examAlgorithm]
  );

  const updateExamCardProperty = useCallback(
    (
      propertyName: keyof SelectedExamCardType,
      value: SelectedExamCardType[keyof SelectedExamCardType]
    ) => {
      setSelectedExamCards((prev: Array<SelectedExamCardType>) => {
        // get the index of the exam to update
        const indexToUpdate = prev.findIndex(
          (exam) =>
            exam.examFieldType === examAlgorithm && exam.groupId === groupId
        );

        // update the correct exam card in the array
        // with the new property value
        if (indexToUpdate > -1) {
          let examToUpdate = prev[indexToUpdate];
          examToUpdate = { ...examToUpdate, [propertyName]: value };
          prev[indexToUpdate] = examToUpdate;
        }

        return [...prev];
      });
    },
    [setSelectedExamCards, groupId, examAlgorithm]
  );

  // handles changing the Both/Right/Left radio buttons on the exam card
  const onChangeEye = useCallback(
    (value: string) => {
      const { LEFT, RIGHT, BOTH } = eyeTypeEnums;
      if (value === BOTH) {
        updateExamCardProperty("eyeCheckboxes", {
          right: true,
          left: true,
        });
      } else if (value === LEFT) {
        updateExamCardProperty("eyeCheckboxes", {
          right: false,
          left: true,
        });
      } else if (value === RIGHT) {
        updateExamCardProperty("eyeCheckboxes", {
          right: true,
          left: false,
        });
      } else {
        updateExamCardProperty("eyeCheckboxes", {
          right: false,
          left: false,
        });
      }
    },
    [updateExamCardProperty]
  );

  // handles changing the Right First toggle on the exam card
  const onChangeSectionOrder = useCallback(
    (isRightFirst: boolean) => {
      updateExamCardProperty("sectionOrder", {
        right: isRightFirst ? 0 : 1,
        left: isRightFirst ? 1 : 0,
      });
    },
    [updateExamCardProperty]
  );

  const onRemoveCardWrapper = useCallback(() => {
    onRemoveCard(examAlgorithm, groupId);
  }, [examAlgorithm, groupId, onRemoveCard]);

  switch (examAlgorithm) {
    case examCardEnums.VISUAL_FIELD_SCREENING.algorithmPath:
      return (
        <VFScreeningCard
          {...props}
          selectedExamCard={selectedExamCard}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.VISUAL_FIELD_THRESHOLDING.algorithmPath:
      return (
        <VFThresholdCard
          {...props}
          selectedExamCard={selectedExamCard}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.VISUAL_FIELD_FULL_120.algorithmPath:
      return (
        <Full120Card
          {...props}
          selectedExamCard={selectedExamCard}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.VISUAL_FIELD_SUPERIOR_36.algorithmPath:
      return (
        <VFSuperior36Card
          {...props}
          selectedExamCard={selectedExamCard}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.VISUAL_FIELD_ESTERMAN.algorithmPath:
      return (
        <VFEstermanCard
          {...props}
          selectedExamCard={selectedExamCard}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.COLOR_VISION.algorithmPath:
      return (
        <ColorVisionCard
          {...props}
          selectedExamCard={selectedExamCard}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.CONTRAST_SENSITIVITY.algorithmPath:
      return (
        <ContrastSensitivityCard
          {...props}
          selectedExamCard={selectedExamCard}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.VISUAL_ACUITY.algorithmPath:
      return (
        <VisualAcuityCard
          {...props}
          selectedExamCard={selectedExamCard}
          onChangeEye={onChangeEye}
          onChangeSectionOrder={onChangeSectionOrder}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.DEVIATION_AND_CORRESPONDENCE.algorithmPath:
      return (
        <DeviationAndCorrespondenceCard
          {...props}
          selectedExamCard={selectedExamCard}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.FUSION_AND_VERGENCE.algorithmPath:
      return (
        <FusionAndVergenceCard
          {...props}
          selectedExamCard={selectedExamCard}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.STEREOPSIS.algorithmPath:
      return (
        <StereopsisCard
          {...props}
          selectedExamCard={selectedExamCard}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.ANISEIKONIA.algorithmPath:
      return (
        <AniseikoniaCard
          {...props}
          selectedExamCard={selectedExamCard}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.RAPD.algorithmPath:
      return (
        <RAPDCard
          {...props}
          selectedExamCard={selectedExamCard}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    case examCardEnums.OCULAR_MOTILITY.algorithmPath:
      return (
        <OcularMotilityCard
          {...props}
          selectedExamCard={selectedExamCard}
          onRemoveCard={onRemoveCardWrapper}
          updateExamCardProperty={updateExamCardProperty}
        />
      );
    default:
      return <></>;
  }
};

export default ExamSliderCard;
